import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { DocumentSingleIcon } from '@grupoboticario/flora-react-icons';
import { app, cart, checkout, order } from 'core';
import { Bff, IS_HYBRID_CART_ACTIVE } from 'core/services';
import { Values } from 'ui/components/templates/Checkout/Views/Summary/components/Summary.components.styles';
import {
  CheckoutHeading,
  CheckoutSkeleton,
  CheckoutView,
  SUMMARY_CONST,
  SummaryAddress,
  SummaryButtonCta,
  SummaryItems,
  SummaryPointsProgram,
  SummaryLoader,
  SummaryPayment,
  SummaryRewards,
  SummaryValues,
} from 'ui/components';
import { useCartShippingModel } from 'core/use-cases/cart/hooks';
import { Modal } from '../Modal';
import { useCheckout } from '../hooks';
import { useGetFeatureFlag } from 'ui/hooks';
import { useDefaultAddress } from 'core/use-cases/shippingModel/selectors';
import { useUser } from 'core/use-cases/user/hooks';
import { actions as shippingModelActions } from 'core/use-cases/shippingModel';
import { STATUS } from 'core/domains/utilities';
import type { UIEvent } from 'react';

const eventAction = 'clique:botao';
const eventCategory = 'carrinho:revisao';
const interactionModal = 'interacao:modal';

const SummaryWidget = () => {
  const dispatch = useDispatch();
  const {
    isSuccess,
    data: { resellerId },
  } = useUser();
  const {
    rewards: { status: processStatus },
    selectedPromotions = [],
    paymentDetail: { status: isSuccessPaymentDetail },
  } = checkout.hooks.useCheckout();
  const { data, isLoading } = checkout.hooks.useSummary();
  const { distributionCenterAddress, shippingModel } = useCartShippingModel();
  const { isCreditCard, isModalOpen, setIsModalOpen, handleOnClose, handleSetStep } = useCheckout();

  const { trackClick } = app.commands.useAppCommand();
  const { completeOrder } = checkout.commands.useCheckoutCommand();

  const { value: isHybridCart, loading } = useGetFeatureFlag(
    IS_HYBRID_CART_ACTIVE,
    false,
    resellerId,
  );
  const fullAddressShippingModel = useDefaultAddress();
  const {
    data: { pointsPrograms = [] },
  } = cart.hooks.useCart();

  const { isLoading: isLoadingOrder } = order.hooks.useOrderDetails();
  const { shippingModelType = '' } = cart.hooks.useCartShippingModel();

  const { paymentPlanType } = checkout.hooks.usePaymentOption();

  const isCentralPayment =
    data.paymentOption.paymentCode === '1' || data.paymentOption.paymentCode === '50';

  useEffect(() => {
    return () => {
      if (isSuccessPaymentDetail === STATUS.ERROR) {
        dispatch(cart.actions.cartFetch.request());
      }
    };
  }, [isSuccessPaymentDetail]);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (isHybridCart && isSuccess && fullAddressShippingModel.length === 0) {
      dispatch(shippingModelActions.availableDeliveryModesFetch.request());

      return;
    }
  }, [loading]);

  const centralPaymentLabel = () => {
    const isPickup = shippingModelType.includes('pickupStore');
    return isPickup ? 'Cartão na retirada do pedido' : 'Cartão no recebimento do pedido';
  };

  const handleChangePaymentMethod = useCallback((event: UIEvent<HTMLButtonElement>) => {
    const label = {
      [Bff.PAYMENT_PLAN_TYPE.CREDIT_CARD]: 'editar-cartao',
      [Bff.PAYMENT_PLAN_TYPE.BANK_BILLING]: 'editar-boleto',
    };

    trackClick(event, 'event', {
      eventAction,
      eventCategory,
      eventLabel: label[paymentPlanType as keyof typeof label] || '',
    });

    if (isCreditCard) {
      setIsModalOpen((prev) => !prev);

      trackClick(event, 'event', {
        eventAction: interactionModal,
        eventCategory,
        eventLabel: `abrir:os-dados-do-cartao-serao-apagados`,
      });

      return;
    }

    handleSetStep(event);
  }, []);

  const handleCompleteOrder = useCallback(() => {
    completeOrder(data.paymentOption);
  }, [data.paymentOption]);

  const getFullAddress = () => {
    if (isHybridCart) {
      return fullAddressShippingModel;
    }

    return data.fullAddress;
  };

  if (isLoading || isLoadingOrder) {
    return <SummaryLoader message="Estamos finalizando o seu pedido" />;
  }

  if (data.cart.isRunning || processStatus === STATUS.RUNNING) {
    return <CheckoutSkeleton />;
  }

  return (
    <CheckoutView testid={SUMMARY_CONST.TEST_ID}>
      <>
        {isModalOpen && (
          <Modal isOpen={isModalOpen} onClose={handleOnClose} contentType="default" />
        )}
        <CheckoutHeading title="Revise o seu pedido" />
        <Values.Title>
          <DocumentSingleIcon size={24} />
          Resumo dos valores
        </Values.Title>
        <SummaryValues values={data.values} />
        <SummaryPayment
          changePaymentMethod={handleChangePaymentMethod}
          description={isCentralPayment ? centralPaymentLabel() : data.paymentOption.name}
        />
        <SummaryAddress
          businessModelCode={shippingModel.businessModelCode}
          distributionCenterAddress={distributionCenterAddress}
          fullAddress={shippingModel.iswithdrawal ? distributionCenterAddress : getFullAddress()}
          isWithdrawal={shippingModel.iswithdrawal}
        />
        <SummaryRewards rewards={data?.rewards} selectedPromotions={selectedPromotions} />
        <SummaryItems items={data.cart.items} />
        <SummaryPointsProgram items={pointsPrograms} />
        <SummaryButtonCta label="Finalizar pedido" onClick={handleCompleteOrder} />
      </>
    </CheckoutView>
  );
};

export default SummaryWidget;
